import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../lib/api';
import { AirplaneTakeoff, Briefcase, Champagne, MapPin, Clock, ShieldCheck, Headset, Users, CaretDown } from '@phosphor-icons/react';

const FAQ_DATA = [
  { q: 'Comment reserver un VTC ?', a: 'Utilisez notre formulaire de reservation en ligne. Selectionnez votre date, heure, depart et destination. Vous recevrez une confirmation immediate.' },
  { q: 'Quels sont vos tarifs ?', a: 'Nos tarifs varient selon le type de vehicule : Berline Confort (1,50EUR/km), Prestige VIP (2,00EUR/km), Minibus Groupe (2,50EUR/km). TVA non applicable.' },
  { q: 'Peut-on annuler une reservation ?', a: 'Oui, l\'annulation est gratuite jusqu\'a 24h avant la prise en charge. Au-dela, des frais peuvent s\'appliquer.' },
  { q: 'Quelles zones desservez-vous ?', a: 'Nous desservons Paris, toute l\'Ile-de-France, ainsi que les aeroports CDG, Orly et Beauvais, et les gares parisiennes.' },
  { q: 'Le temps d\'attente est-il inclus ?', a: 'Pour les transferts aeroport, 60 minutes d\'attente gratuite sont incluses. Pour les autres courses, vous pouvez ajouter du temps d\'attente lors de la reservation.' },
  { q: 'Comment payer ?', a: 'Nous acceptons le paiement par carte bancaire en ligne via notre plateforme securisee Stripe. Le paiement par virement est possible sur demande.' },
];

const SERVICES = [
  {
    icon: AirplaneTakeoff, title: 'Transferts Aeroport',
    desc: 'CDG, Orly, Beauvais. Suivi des vols en temps reel. 60 minutes d\'attente gratuite incluses.',
    bullets: ['Accueil personnalise avec panneau nominatif', 'Suivi des retards de vol', 'Aide aux bagages', 'Wi-Fi à bord'],
  },
  {
    icon: Briefcase, title: 'Transport d\'Affaires',
    desc: 'Deplacements professionnels, rendez-vous clients, seminaires. Wi-Fi et facturation mensuelle disponibles.',
    bullets: ['Vehicules avec Wi-Fi haut debit', 'Facturation mensuelle possible', 'Chauffeur discret et ponctuel', 'Service de mise à disposition'],
  },
  {
    icon: Champagne, title: 'Evenements & Soirées',
    desc: 'Mariages, galas, soirées. Service sur mesure et devis personnalisé.',
    bullets: ['Vehicules decores sur demande', 'Mise à disposition longue durée', 'Coordination avec les organisateurs', 'Devis personnalisé gratuit'],
  },
];

export default function Services() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    api.get('/vehicles')
  .then(r => {
    // Si r.data est un objet contenant le tableau, utilisez la bonne clé
    const data = Array.isArray(r.data) ? r.data : r.data.vehicles; 
    setVehicles(data);
  })
  .catch(() => setVehicles([]));
  }, []);

  return (
    <div className="pt-20" data-testid="services-page">
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-xs uppercase tracking-[0.2em] text-desti-gold mb-3">Nos prestations</p>
            <h1 className="font-heading text-4xl sm:text-5xl text-white">Services VTC Premium</h1>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            {SERVICES.map((s, i) => (
              <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-desti-surface border border-white/5 p-8 hover:-translate-y-1 transition-transform duration-300" data-testid={`service-card-${i}`}>
                <s.icon size={36} weight="duotone" className="text-desti-gold mb-5" />
                <h3 className="font-heading text-xl text-white mb-3">{s.title}</h3>
                <p className="text-sm text-desti-text mb-4">{s.desc}</p>
                <ul className="space-y-2">
                  {s.bullets.map((b) => (
                    <li key={b} className="text-xs text-white/50 flex items-start gap-2">
                      <span className="w-1 h-1 bg-desti-gold rounded-full mt-1.5 shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-desti-surface/50" data-testid="fleet-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-desti-gold mb-3">Notre flotte</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-white">Des vehicules d'exception</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {Array.isArray(vehicles) && vehicles.map((v, i) => (
              <motion.div key={v.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-desti-surface border border-white/5 overflow-hidden hover:-translate-y-1 transition-transform duration-300" data-testid={`fleet-card-${i}`}>
                <img src={v.imageUrl} alt={v.name} className="w-full h-48 object-cover" />
                <div className="p-6">
                  <h3 className="font-heading text-xl text-white">{v.name}</h3>
                  <p className="text-sm text-desti-text mt-2">{v.description}</p>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
                    <span className="text-xs text-white/50 flex items-center gap-1"><Users size={14} /> {v.seats} places</span>
                    <span className="text-desti-gold font-medium">{v.pricePerKm.toFixed(2)} EUR/km</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-desti-gold mb-3">Nos engagements</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-white">Garanties de service</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Clock, title: 'Ponctualite', desc: 'Toujours a l\'heure, sans exception.' },
              { icon: ShieldCheck, title: 'Securite', desc: 'Vehicules controles, chauffeurs experimentes.' },
              { icon: MapPin, title: 'Transparence', desc: 'Prix connu a l\'avance, pas de surprise.' },
              { icon: Headset, title: 'Support 24/7', desc: 'Service client disponible a tout moment.' },
            ].map((g, i) => (
              <div key={g.title} className="text-center p-6" data-testid={`guarantee-${i}`}>
                <g.icon size={32} weight="duotone" className="text-desti-gold mx-auto mb-4" />
                <h3 className="font-heading text-lg text-white mb-2">{g.title}</h3>
                <p className="text-sm text-desti-text">{g.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-desti-surface/50" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-desti-gold mb-3">FAQ</p>
            <h2 className="font-heading text-3xl text-white">Questions frequentes</h2>
          </div>
          <div className="space-y-3">
            {FAQ_DATA.map((faq, i) => (
              <div key={i} className="border border-white/5 bg-desti-surface" data-testid={`faq-item-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left">
                  <span className="text-sm text-white">{faq.q}</span>
                  <CaretDown size={16} className={`text-desti-gold transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4">
                    <p className="text-sm text-desti-text leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl text-white mb-6">Pret a reserver ?</h2>
          <div className="flex flex-wrap gap-4 justify-center">
            <button onClick={() => navigate('/')} className="bg-desti-gold text-desti-black font-medium text-sm tracking-wide px-8 py-4 hover:bg-desti-gold-hover transition-colors" data-testid="services-cta-book">
              Reserver en ligne
            </button>
            <button onClick={() => navigate('/contact')} className="border border-white/20 text-white text-sm tracking-wide px-8 py-4 hover:bg-white/5 hover:border-desti-gold transition-colors" data-testid="services-cta-contact">
              Demander un devis
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
